import { useEffect, useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, CalendarDays, Mail } from "lucide-react";
import { z } from "zod";
import { signInWithGoogle, signInWithMicrosoft } from "../lib/auth";
import { ensureMe } from "../lib/queries";
import { safeRedirect } from "../lib/redirect";

/**
 * Getting in, in two beats.
 *
 * It was one screen carrying the tagline, the positioning paragraph, the
 * button and the scope explanation all at once — so the first thing a founder
 * met was a wall, and the sentence that mattered ("we read your calendar and
 * your mail") sat underneath the button as fine print, which is the worst
 * place for the only sentence that asks her for something.
 *
 * Now: who we are, then what we read. Each screen holds one idea and at most
 * three sentences.
 *
 * **Still one consent trip, even with Skip.** AGENTS.md rule #6 rejected
 * incremental authorisation because a second trip through Google is where
 * people stop, and that reasoning is intact: neither button here asks twice.
 * She is not signed in yet, so *whichever* she presses is her first and only
 * handoff — "Allow and continue" sends identity plus the two read scopes and
 * shows one screen with everything on it, "Skip for now" sends identity alone
 * and shows one screen without them. The second trip exists only for the
 * founder who skipped and later connects from Settings, and she chose it.
 *
 * What did change: the provider in apps/api/src/auth.ts no longer carries the
 * calendar and mail scopes. Putting them back there would silently break Skip
 * — they would ride along on every sign-in, and the button would keep working
 * while asking for exactly what it says it does not.
 *
 * Living at `?step=access` rather than its own route is deliberate: the
 * signed-in guard, the `redirect` target that survives the round trip, and the
 * bare shell in __root.tsx are all keyed to `/login`, and a second route would
 * have to duplicate all three.
 */
export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional(),
    step: z.literal("access").optional(),
    /**
     * What Google said when it refused, handed back by better-auth's callback
     * (see `errorCallbackURL` in lib/auth.ts). Kept in the schema because
     * TanStack drops search params it does not declare — which is precisely
     * how a refusal used to arrive as a blank screen.
     */
    error: z.string().max(200).optional(),
    error_description: z.string().max(500).optional(),
    /** Which handoff the access screen is about. Carried in the URL rather
     *  than in state so the phone's back gesture — and a refresh — land on the
     *  screen she was actually on. */
    provider: z.enum(["google", "microsoft"]).optional(),
  }),
  beforeLoad: async ({ context, search }) => {
    // Already signed in: this page has nothing to offer. Send her on.
    const me = await ensureMe(context.queryClient);
    if (me) throw redirect({ to: safeRedirect(search.redirect), replace: true });
  },
  component: Login,
});

/**
 * Google's refusal codes, in words a founder can act on.
 *
 * `access_denied` has two causes that look identical from here and read very
 * differently to her: she dismissed the consent screen, or Google refused the
 * account outright because the app's review is still pending and she is not on
 * the tester list. Naming both is honest; guessing which one would not be.
 *
 * Anything else is shown as itself rather than smoothed into "something went
 * wrong" — an unfamiliar code she can quote is worth more than a sentence that
 * fits every failure equally badly.
 */
function googleRefusal(code: string, description?: string): string {
  if (code === "access_denied") {
    return "Google did not hand over the connection. That happens if you closed the consent screen — or if this account does not have access yet while Omina's Google review is pending.";
  }
  const detail = description?.trim();
  return detail
    ? `Google turned the sign-in down: ${detail} (${code})`
    : `Google turned the sign-in down (${code}). Try again, or use another account.`;
}

function Login() {
  const {
    redirect: target,
    step,
    error: refusedCode,
    error_description: refusedDetail,
    provider = "google",
  } = Route.useSearch();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A refusal comes back on the URL, so it survives the round trip that local
  // component state cannot. It seeds the same banner a local failure uses.
  useEffect(() => {
    if (refusedCode) setError(googleRefusal(refusedCode, refusedDetail));
  }, [refusedCode, refusedDetail]);

  /**
   * Is there a dev sign-in on this server?
   *
   * Asked rather than assumed from the hostname: the answer is the same
   * `isLocalDev` gate the route itself uses, so the button cannot appear
   * anywhere the route would 404.
   */
  const [devAuth, setDevAuth] = useState(false);
  useEffect(() => {
    let live = true;
    fetch("/api/health")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { devAuth?: boolean } | null) => {
        if (live && d?.devAuth) setDevAuth(true);
      })
      .catch(() => {
        /* health is a convenience here; sign-in works without it */
      });
    return () => {
      live = false;
    };
  }, []);

  /**
   * Both buttons on the access screen end up here; `integrations` is the only
   * difference between them.
   *
   * Each is a single trip through Google. Connecting shows one consent screen
   * carrying identity plus the two read scopes; skipping shows the same screen
   * without them. Nobody is asked twice — the founder who skips and connects
   * later from Settings makes the second trip, and she is the one who chose to.
   */
  async function go(integrations: boolean, provider: "google" | "microsoft" = "google") {
    setBusy(true);
    setError(null);
    try {
      /**
       * An explicit `redirect` still wins: she was going somewhere specific
       * before the guard sent her here, and onboarding does not get to
       * override that.
       */
      // Interests either way. It used to fork here — connectors to curation,
      // skippers to interests — which made the two answers to one question
      // produce two different products, and made the stated-preference column
      // exist only for founders whose calendar Omina could not read.
      //
      // Interests is now the one question everybody answers, and the fork
      // moves to the end of it, where curation is offered to whoever actually
      // has ninety days of rooms to curate. `/interests` reads the grant
      // itself rather than being told, so the branch cannot disagree with what
      // Google actually gave.
      const home = target ? safeRedirect(target) : "/interests";

      // Hands off to Google; on success the browser comes back to callbackURL
      // with a first-party session cookie set by the same Worker that serves
      // this page.
      await (provider === "microsoft"
        ? signInWithMicrosoft(home, { integrations })
        : signInWithGoogle(home, { integrations }));
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : "Sign-in failed. Try again.");
    }
  }

  if (step === "access") {
    return (
      <AccessStep
        busy={busy}
        error={error}
        provider={provider}
        onAllow={() => void go(true, provider)}
        onSkip={() => void go(false, provider)}
        devAuth={devAuth}
        /**
         * Interests, like every other way through this screen. A dev session
         * is minted locally and never goes near Google, so it carries no
         * calendar scopes — which the fork at the end of interests reads for
         * itself, and correctly sends her past curation.
         */
        devHref="/api/dev/session?to=%2Finterests"
      />
    );
  }

  return (
    <WelcomeStep
      error={error}
      onMicrosoft={() =>
        navigate({ to: "/login", search: { redirect: target, step: "access", provider: "microsoft" } })
      }
      devAuth={devAuth}
      /**
       * Same destination the Google buttons use, for the same reason: this is
       * the only way onto the flow from a phone, so landing it on `/home`
       * meant onboarding could not be walked on the device it was designed
       * for. A deep link she was chasing still wins.
       */
      devHref={`/api/dev/session?to=${encodeURIComponent(target ? safeRedirect(target) : "/interests")}`}
      onContinue={() =>
        // `replace: false` on purpose — the phone's back gesture should return
        // to the welcome screen, not leave the app.
        navigate({ to: "/login", search: { redirect: target, step: "access" } })
      }
    />
  );
}

/**
 * Beat one: her sentence, not ours.
 *
 * The headline is written in the founder's first person and the line under it
 * answers her. That is the whole design: every version before this one opened
 * by describing the product — a tagline, then a positioning line, then a note
 * about Google — so the first screen was Omina talking about itself to someone
 * who had not said anything yet. Putting her want on top and Omina's reply
 * under it makes the first moment a recognition rather than a pitch, which is
 * what makes it feel warm.
 *
 * Nothing else belongs here. The Google explanation moved to the second screen
 * where it is actually the subject; this one carries a want, an answer and a
 * button.
 */
function WelcomeStep({
  onContinue,
  onMicrosoft,
  devAuth,
  devHref,
  error,
}: {
  onContinue: () => void;
  onMicrosoft: () => void;
  devAuth: boolean;
  devHref: string;
  error: string | null;
}) {
  return (
    <div className="card authcard">
      <h1 className="tk tk-want">Ready to find the right rooms for me</h1>

      <button type="button" className="authbtn" onClick={onContinue}>
        <GoogleMark />
        Continue with Google
      </button>

      {/*
        Outlook, quieter than Google but not hidden.
 
        Two real doors, not a primary and a fallback: a founder whose work
        lives at Microsoft has no Google account to fall back to, and burying
        this behind "more options" would read to her as the product not being
        for her. Google leads only because it is the larger half of the room.
      */}
      <button type="button" className="authskip" onClick={onMicrosoft}>
        Continue with Microsoft
      </button>

      {/* A refusal from either provider lands back here, not on the access
          screen, so this is where the sentence has to be — without it the
          round trip ends on an unchanged page and reads as a button that does
          nothing. */}
      {error && <div className="danger">{error}</div>}

      {/*
        Development only, and only ever on a local server (see `devAuth`).

        A plain `<a>`, not a fetch: the route answers with a `Set-Cookie` and a
        redirect, so letting the browser follow it is the whole mechanism.

        It exists because Google is a dead end on any device that is not this
        Mac. `wrangler dev` pins the Host to `localhost:8787` and that is the
        only redirect_uri registered, so the consent screen succeeds and then
        sends the phone to an address that means *the phone*. Without this
        button the sign-in screen is a trap on every phone and tablet, and it
        looks like a broken app rather than like development.
      */}
      {devAuth && (
        <>
          <a className="authskip" href={devHref}>
            Sign in on this device
          </a>
          <p className="hint">
            Google needs a browser on the machine running the server. On a phone,
            use the line above.
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Beat two: what she is handing over, before Google asks for it.
 *
 * Every claim here is checked against what the code actually does, because a
 * permissions screen is the one place an optimistic sentence becomes a lie:
 *
 *  - "read-only" — the scopes are `calendar.readonly` and `gmail.readonly`.
 *  - "who and when, never what you wrote" — Gmail is stored as metadata only,
 *    ninety days, no bodies (AGENTS.md rule #6d). A body is fetched live when
 *    she opens a message and never written down.
 *  - there is no claim about disconnecting in Settings. No Google disconnect
 *    route exists in the API today — only LinkedIn has one — and pointing her
 *    at a button that is not there is worse than saying nothing.
 *
 * **Known gap, removed deliberately and worth restoring somewhere.** This
 * screen used to warn that Google would call Omina unverified.
 * `gmail.readonly` is a *restricted* scope, so until a CASA assessment Google
 * shows a full-page interstitial that looks exactly like a phishing block. The
 * warning was cut to keep this screen warm, which is a defensible trade only
 * because it is the founder's own account today. Before anyone else signs up,
 * that sentence needs a home — on Google's own screen there is nothing telling
 * her it is expected, and a founder who turns around there never comes back.
 */
function AccessStep({
  busy,
  error,
  provider,
  onAllow,
  onSkip,
  devAuth,
  devHref,
}: {
  busy: boolean;
  error: string | null;
  provider: "google" | "microsoft";
  onAllow: () => void;
  onSkip: () => void;
  devAuth: boolean;
  devHref: string;
}) {
  return (
    <div className="card authcard">
      {/* Keep the headline short and free of hyphens: a display serif at this
          size has room for about twelve characters a line on a 375px phone,
          and an earlier "Two things, read-only" broke across its own hyphen
          ("read-" / "only"). Qualifiers belong in the lede, where wrapping
          costs nothing. */}
      <div className="eyebrow">One more step</div>
      <h1 className="tk">Connect your calendar and email</h1>
      <p className="lede">
        Omina reads them the way a chief of staff would: to know where you
        already go, and which rooms are worth your time next.
      </p>

      <ul className="grants">
        <li className="grant">
          <span className="granticon" aria-hidden>
            <CalendarDays size={16} />
          </span>
          <span className="granttext">
            <b>Your calendar</b>
            <span>the rooms you are walking into</span>
          </span>
        </li>
        <li className="grant">
          <span className="granticon" aria-hidden>
            <Mail size={16} />
          </span>
          <span className="granttext">
            <b>Your mail</b>
            <span>
              {provider === "microsoft"
                ? "who answered, and when. Microsoft withholds the message itself"
                : "who answered, and when. Never what you wrote"}
            </span>
          </span>
        </li>
      </ul>

      <button type="button" className="authbtn" onClick={onAllow} disabled={busy}>
        {provider === "microsoft" ? <MicrosoftMark /> : <GoogleMark />}
        {busy
          ? `Opening ${provider === "microsoft" ? "Microsoft" : "Google"}…`
          : "Allow and continue"}
      </button>

      {/*
        Google shows an "unverified app" interstitial for `gmail.readonly`
        until a CASA assessment clears it, and a founder who meets that screen
        with no warning reads it as "this is unsafe" and stops. Said plainly
        here, before she sees it, it is a pending review instead of an alarm —
        and it stays factual: the scopes really are read-only.
      */}
      <p className="hint">
        {provider === "microsoft"
          ? "If your work account is managed, Microsoft may ask an administrator to approve Omina before this can finish. Both permissions are read-only."
          : "Google may show an “unverified app” notice while Omina’s review is pending — choose Advanced, then Continue. Both scopes are read-only."}
      </p>

      {error && <div className="danger">{error}</div>}

      {/*
        Skip signs her in without the two read scopes.

        Deliberately quiet, and deliberately real. Quiet because connecting is
        the path that makes the product work and the two should not look like
        equal choices. Real because it is a different Google request, not a
        cosmetic one: a "skip" that handed over the calendar anyway would be
        the worst sentence on this screen.

        It says "for now" because that is the true shape of it. Nothing is
        lost — Settings carries a connect card, and connecting later grants the
        union of everything, so she is never worse off for having waited.
      */}
      <button type="button" className="authskip" onClick={onSkip} disabled={busy}>
        Skip for now
      </button>

      {/*
        Development only. Both buttons above hand off to Google — "Skip for
        now" asks for fewer scopes, but it is still an OAuth round trip — and
        that round trip always ends at `localhost:8787`, which on a phone means
        the phone. So on a device this screen has two controls and no exit, and
        skipping looks like it "sends me to the same Google connection".
      */}
      {devAuth && (
        <a className="authskip" href={devHref}>
          Skip on this device
        </a>
      )}

      {/* A link, not a second button: going back is a way out, and it should
          not compete with the way forward. */}
      <Back />
    </div>
  );
}

/**
 * A router navigation rather than `history.back()`: this screen is reachable
 * by its own URL, and on a deep link "back" through history leaves the app
 * entirely. Navigating to the welcome step always means the welcome step.
 */
function Back() {
  const { redirect: target } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="authback"
      onClick={() => navigate({ to: "/login", search: { redirect: target } })}
    >
      <ArrowLeft size={13} aria-hidden />
      Back
    </button>
  );
}

/** Google's mark, inline. An <img> would be a request to a third-party CDN on
 *  the one page that must render instantly and offline-ish. */
/**
 * Google's "G", in one colour.
 *
 * The four brand colours are dropped in favour of `currentColor`, so the mark
 * takes the button's ink like every other icon in the product. On a warm paper
 * page the four-colour G was the single most machine-made object on the screen,
 * and it sat on the one page whose whole job is to feel like a door rather than
 * a form.
 *
 * This is within Google's identity guidelines rather than a liberty taken with
 * them: they publish the mark for exactly this use in a neutral single colour
 * alongside the full-colour one. What is *not* allowed — and what this
 * carefully does not do — is recolour it into some third palette of ours. One
 * ink, inherited, is the sanctioned monochrome treatment.
 *
 * The paths are unchanged: same four shapes, same geometry, one fill.
 */
/** Microsoft's four squares. Same reasoning as the Google mark below: an icon
 *  CDN would be a runtime dependency the fonts were self-hosted to avoid. */
function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 48 48"
      fill="currentColor"
      aria-hidden
      focusable="false"
    >
      <path d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}
